import { v4 as uuidv4 } from 'uuid';
import { Event, sendErrorEvent, safeLocalStorage, getUserAgentInfo } from '@fluidx/utilities';
import { BaseLogger } from '../BaseLogger';
import { consoleLogProcessor } from '../LogProcessor/ConsoleLogProcessor';
import { ITelemetryProperties } from '@fluidframework/common-definitions';
import { LogProcessor, LoggerContext, UserContext, AriaInfo } from '../LogProcessor';
import { OfficeFluidAriaInfo, OfficeFluidTestAriaInfo } from './OfficeFluidAriaInfo';
import { HostContext, Audience } from '@fluidx/office-fluid-types';

interface BufferedEvent {
  type: 'event';
  entry: Event;
}

interface OfficeFluidLoggerContext {
  type: 'context';
  entry: LoggerContext;
}

type BufferEntry = BufferedEvent | OfficeFluidLoggerContext;

interface BufferedLogger {
  /**
   * Sets special, well-known persistent fields for User Identity on the events logged through the logger.
   * These fields have to be logged in a certain way by the logProcessor. For example, the OtelLogProcessor converts these fields such that it follows the Office Schema.
   */
  setUserContext: (context: UserContext) => void;

  /**
   * Loads the full logger, replays buffered events on a log processor and stops buffering events in memory.
   * Invoking this for a second or higher time will be a no-op.
   */
  loadFull: () => Promise<void>;
}

/**
 * Parameters to the Office Fluid Logger
 */
interface OfficeFluidLoggerConfig {
  /**
   * Host specific fields that get logged with every event
   */
  hostContextProps: HostContext;
  /**
   * Properties that get logged with every event
   */
  persistentProps?: ITelemetryProperties;
  /**
   * The namespace of the logger
   */
  namespace?: string;
  /**
   * The parameter that defines if hosts want to delay the call to load the OtelLogProcessor.
   * Hosts are expected to call the loadFull method if this param is set to true
   */
  delayLoadLogger?: boolean;
}

/**
 * VSO 4382315: Make HostSessionId field in hostContext required
 * Remove this fallback when we make HostSessionId required
 * initializing here so it doesn't change with new officeFluidLogger initialization
 */
const fallbackSessionId = uuidv4();

/**
 * Implementation of the Logger interface that buffers events and delegates events to a LogProcessor once it is fully loaded.
 */
export class OfficeFluidLogger extends BaseLogger implements BufferedLogger {
  private buffer: BufferEntry[] = [];
  private logProcessor: LogProcessor | undefined;
  private fullyLoaded = false;
  private loadFullCalled = false;
  private readonly ariaInfo: AriaInfo;
  private audience: Audience;

  /**
   * Constructor to initialize the Logger.
   */
  constructor(config: OfficeFluidLoggerConfig) {
    super(
      {
        ...config.persistentProps,
        hostCorrelationId: config.hostContextProps.hostCorrelationId,
        hostScenarioName: config.hostContextProps.hostScenarioName,
        /**
         * We can delete this after hosts have updated their telemetry package
         * to change the sessionId from a UUID to the HostSessionId
         */
        hostSessionId: config.hostContextProps.hostSessionId,
        hostName: config.hostContextProps.hostName,
        /**
         * 1DS doesn't pass the osName and osVersion to the events, and we currently don't have a way
         * to pass these as Part A fields so pass them as persistentProps instead
         */
        osName: getUserAgentInfo().osName,
        osVersion: getUserAgentInfo().osVersion
      },
      config.namespace
    );

    const { hostContextProps, delayLoadLogger } = config;

    this.audience = hostContextProps.audience;
    this.ariaInfo = this.getAriaInfoFromAudience();

    // Pushing initialization context straight to the buffer because we know at this point loadFull hasn't been called
    this.buffer.push({
      type: 'context',
      entry: {
        appVersion: hostContextProps.hostAppVersion,
        user: hostContextProps.userContext.user,
        /**
         * VSO 4382315: Make HostSessionId field in hostContext required
         * Remove this fallback when we make HostSessionId required
         * If host doesn't provide a sessionId, set it as the fallbackSessionId
         */
        sessionId: hostContextProps.hostSessionId || fallbackSessionId
      }
    });

    if (!delayLoadLogger) {
      this.loadFull().catch((error) => {
        sendErrorEvent(this, { eventName: 'LoggerLoadFailure' }, error);
      });
    }
  }

  private getAriaInfoFromAudience() {
    // If the audience is Local or Test send the telemetry to the test tenant
    // Task 4484308: Clean up old Audience Values
    if (this.audience === 'Local' || this.audience === 'Test' || this.audience === 'Automation') {
      return OfficeFluidTestAriaInfo;
    }
    return OfficeFluidAriaInfo;
  }

  public setUserContext(context: UserContext) {
    if (!this.fullyLoaded) {
      this.buffer.push({ type: 'context', entry: context });
    } else {
      this.setContextOnProcessor(context);
    }
  }

  public async loadFull() {
    if (this.loadFullCalled) {
      return;
    }

    this.loadFullCalled = true;

    // Help reduce the initial boot slice by pulling down Otel bits only when needed.
    const logProcessorPromise = import(/* webpackChunkName: "otel" */ '../LogProcessor/OtelLogProcessor').then((code) =>
      this.registerProcessor(new code.OtelLogProcessor(this.ariaInfo!, this.audience))
    );

    return logProcessorPromise.then(() => {
      this.buffer = [];
      this.fullyLoaded = true;
    });
  }

  /**
   * Prevents new events from being added for transmission. It also batches all
   * events currently in the queue, and creates requests for them to be sent.
   * Make sure that this is called after logging events related to session end.
   */
  public shutdown() {
    this.loadFull()
      .then(() => {
        this.logProcessor?.shutdown();
      })
      .catch(() => {});
  }

  protected logEvent(event: Event): void {
    // In addition to sending the event to the registered logProcessor, log to console if setting is enabled.
    // TODO: Investigate using the debug library to do the styling in the future.
    const forceLogToConsole = safeLocalStorage()?.getItem('debugPurple') === '1';
    if (forceLogToConsole) {
      consoleLogProcessor.logEvent(event);
    }

    if (!this.fullyLoaded) {
      this.buffer.push({ type: 'event', entry: event });
    } else {
      this.sendToProcessor(event);
    }
  }

  private registerProcessor(logProcessor: LogProcessor) {
    this.logProcessor = logProcessor;
    this.buffer.forEach((item) => {
      if (item.type === 'event') {
        this.sendToProcessor(item.entry);
      } else {
        this.setContextOnProcessor(item.entry);
      }
    });
  }

  private sendToProcessor(event: Event) {
    if (this.logProcessor) {
      this.logProcessor!.logEvent(event);
    }
  }

  private setContextOnProcessor(context: LoggerContext) {
    if (this.logProcessor) {
      this.logProcessor!.setContext(context);
    }
  }
}

// cspell:ignore otel
